// Valid values for component props
const VALID_VARIANTS = [
  "text-over-image",
  "full-bleed-text-over-image",
  "text-and-image-blocks",
]
const VALID_TEXT_PLACEMENTS = ["center", "left"]
const VALID_PHOTO_OVERLAY_DIRECTIONS = [
  "bottom-top",
  "top-bottom",
  "left-right",
  "right-left",
  "tint",
]
const VALID_PHOTO_OVERLAY_OPACITIES = [10, 15, 20, 25, 30, 35, 40, 45]

/**
 * Validates LargeImageAndTextBlock component props
 * @param {object} props - The props object to validate
 * @param {boolean} [throwOnError=false] - If true, throws an error on validation failure. If false, returns validation result
 * @return {{isValid: boolean, errors: string[]}} ValidationResult object containing isValid flag and array of error messages
 * @throws {Error} if throwOnError is true and validation fails
 */
export const validateLargeImageAndTextBlockProps = (
  props,
  throwOnError = false
) => {
  const errors = []

  // Check if props is an object
  if (!props || typeof props !== "object") {
    errors.push("Props must be an object")
    if (throwOnError) {
      throw new Error(
        `LargeImageAndTextBlock validation failed:\n${errors.join("\n")}`
      )
    }
    return { isValid: false, errors }
  }

  // Validate required image
  if (!props.image) {
    errors.push("image is required")
  } else {
    if (typeof props.image !== "object") {
      errors.push("image must be an object")
    } else {
      if (!props.image.src) {
        errors.push("image.src is required")
      }
    }
  }

  // Validate optional variant
  if (props.variant !== undefined) {
    if (!VALID_VARIANTS.includes(props.variant)) {
      errors.push(`variant must be one of: ${VALID_VARIANTS.join(", ")}`)
    }
  }

  // Validate optional textPlacement
  if (props.textPlacement !== undefined) {
    if (!VALID_TEXT_PLACEMENTS.includes(props.textPlacement)) {
      errors.push(
        `textPlacement must be one of: ${VALID_TEXT_PLACEMENTS.join(", ")}`
      )
    }
  }

  // Validate optional showHeader
  if (props.showHeader !== undefined) {
    if (typeof props.showHeader !== "boolean") {
      errors.push("showHeader must be a boolean if provided")
    }
  }

  // Validate optional heading
  if (props.heading !== undefined) {
    if (typeof props.heading !== "object" || props.heading === null) {
      errors.push("heading must be an object if provided")
    } else {
      if (!props.heading.text) {
        errors.push("heading.text is required when heading is provided")
      }
      if (
        typeof props.heading.text !== "string" &&
        typeof props.heading.text !== "object"
      ) {
        errors.push("heading.text must be a string or React element")
      }
    }
  }

  // Validate optional copy
  if (props.copy !== undefined) {
    if (typeof props.copy !== "object" || props.copy === null) {
      errors.push("copy must be an object if provided")
    } else {
      if (!props.copy.text) {
        errors.push("copy.text is required when copy is provided")
      }
      if (
        typeof props.copy.text !== "string" &&
        typeof props.copy.text !== "object"
      ) {
        errors.push("copy.text must be a string or React element")
      }
    }
  }

  // Validate optional CTAs
  if (props.CTAs !== undefined) {
    if (!Array.isArray(props.CTAs)) {
      errors.push("CTAs must be an array if provided")
    } else {
      props.CTAs.forEach((cta, index) => {
        if (typeof cta !== "object" || cta === null) {
          errors.push(`CTAs[${index}] must be an object`)
        } else {
          if (!cta.text) {
            errors.push(`CTAs[${index}].text is required`)
          }
          if (!cta.href && !cta.onClick) {
            errors.push(`CTAs[${index}] must have either href or onClick`)
          }
        }
      })
    }
  }

  // Validate optional gradientDirection
  if (props.gradientDirection !== undefined) {
    if (!VALID_PHOTO_OVERLAY_DIRECTIONS.includes(props.gradientDirection)) {
      errors.push(
        `gradientDirection must be one of: ${VALID_PHOTO_OVERLAY_DIRECTIONS.join(
          ", "
        )}`
      )
    }
  }

  // Validate optional opacity
  if (props.opacity !== undefined) {
    if (!VALID_PHOTO_OVERLAY_OPACITIES.includes(props.opacity)) {
      errors.push(
        `opacity must be one of: ${VALID_PHOTO_OVERLAY_OPACITIES.join(", ")}`
      )
    }
  }

  // Validate optional topSpacer
  if (props.topSpacer !== undefined) {
    if (typeof props.topSpacer !== "object" || props.topSpacer === null) {
      errors.push("topSpacer must be an object if provided")
    }
  }

  // Validate optional bottomSpacer
  if (props.bottomSpacer !== undefined) {
    if (typeof props.bottomSpacer !== "object" || props.bottomSpacer === null) {
      errors.push("bottomSpacer must be an object if provided")
    }
  }

  const isValid = errors.length === 0

  if (!isValid && throwOnError) {
    throw new Error(
      `LargeImageAndTextBlock validation failed:\n${errors.join("\n")}`
    )
  }

  return { isValid, errors }
}
